/** The /contact page: how to reach the shop, where it is, when it is open. Facts live in src/shop-contact.ts. */
import { useEffect, useState } from "react";
import { hoursRows, mapEmbedUrl, openStatus, resolveContact, telHref, waHref } from "../../../src/shop-contact";
import { Go, type GoFn } from "./route";
import { setSeo } from "./seo";
import { ChannelButton, SocialLinks } from "./Subscribe";
import type { ShopConfig } from "./types";

const EXT = { target: "_blank", rel: "noopener noreferrer" } as const;
/** "0792551394" → "079 255 1394" for reading; anything not a 10-digit local number is shown as stored. */
const spaced = (n: string): string => (/^0\d{9}$/.test(n.replace(/\s/g, "")) ? n.replace(/\s/g, "").replace(/^(\d{3})(\d{3})(\d{4})$/, "$1 $2 $3") : n);
import { GOOGLE_REVIEWS_URL } from "./Footer";

export function ContactPage({ config, go }: { config: ShopConfig | null; go: GoFn }) {
  const { contact, collect, hours } = resolveContact(config);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    setSeo("Contact us", `Find Z S MERCH: ${collect.address}. WhatsApp, phone, email and trading hours.`);
  }, [collect.address]);
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(t);
  }, []);
  const status = openStatus(hours, now);
  const wa = waHref(contact.whatsapp);
  const tel = telHref(contact.phone);
  return (
    <div className="wrap page-pad contact">
      <nav className="crumbs" aria-label="Breadcrumb"><ol><li><Go to={{ name: "home" }} go={go}>Home</Go></li><li aria-current="page">Contact</li></ol></nav>
      <h1>Contact us</h1>
      <div className="contact-grid">
        <section className="panel" aria-labelledby="c-talk">
          <h2 id="c-talk">Talk to us</h2>
          <ul className="contact-list">
            {wa && <li><a className="btn btn-wa" href={wa} {...EXT}>WhatsApp {spaced(contact.whatsapp)}<span className="sr-only"> (opens in a new tab)</span></a></li>}
            {tel && <li><a className="btn btn-quiet" href={tel}>Call {spaced(contact.phone)}</a></li>}
            <li><a href={`mailto:${contact.email}`}>{contact.email}</a></li>
          </ul>
          <h2>Follow us</h2>
          <SocialLinks />
          <p><ChannelButton className="btn btn-quiet" /></p>
        </section>
        <section className="panel" aria-labelledby="c-visit">
          <h2 id="c-visit">Collect or visit</h2>
          <p className="contact-addr"><b>{collect.name}</b><br />{collect.address}</p>
          <p><a className="btn btn-action" href={collect.mapUrl} {...EXT}>Get directions<span className="sr-only"> (opens in a new tab)</span></a></p>
          <p><a href={GOOGLE_REVIEWS_URL} {...EXT}>See our reviews on Google ★<span className="sr-only"> (opens in a new tab)</span></a></p>
          <h3>Trading hours</h3>
          <p className={`open-now${status.open ? " is-open" : ""}`} role="status"><i aria-hidden="true" />{status.text}</p>
          <table className="hours"><tbody>{hoursRows(hours).map((r) => <tr key={r.days}><th scope="row">{r.days}</th><td>{r.times}</td></tr>)}</tbody></table>
        </section>
      </div>
      <div className="map-frame">
        <iframe title={`Map showing ${collect.address}`} src={mapEmbedUrl(collect.address)} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
      </div>
    </div>
  );
}
